import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Alert,
  Switch,
  FormControlLabel,
  Avatar,
  CircularProgress,
} from '@mui/material';
import { Grid } from '@mui/material';

import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PhotoCamera as PhotoCameraIcon,
  Delete as DeletePhotoIcon,
} from '@mui/icons-material';
import { apiService, Panelist, Voice } from '../services/api';

const Panelists: React.FC = () => {
  const [panelists, setPanelists] = useState<Panelist[]>([]);
  const [voices, setVoices] = useState<Voice[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPanelist, setEditingPanelist] = useState<Panelist | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    gender: 'female',
    age: 30,
    ttsProvider: 'google',
    voiceId: '',
    voiceName: '',
    languageCode: 'en-US',
    character: '',
    background: '',
    expertise: [] as string[],
    discussionStyle: {
      tone: 'professional',
      pace: 'moderate',
      style: 'informative'
    },
    isPublic: true,
  });
  const [error, setError] = useState('');
  const [uploadingHeadshot, setUploadingHeadshot] = useState(false);
  const [deletingHeadshot, setDeletingHeadshot] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchPanelists();
    fetchVoices();
  }, []);

  const fetchPanelists = async () => {
    try {
      const panelistsData = await apiService.getPanelists();
      setPanelists(panelistsData);
    } catch (error) {
      console.error('Error fetching panelists:', error);
      setError('Failed to load panelists');
    } finally {
      setLoading(false);
    }
  };

  const fetchVoices = async () => {
    try {
      const voicesData = await apiService.getVoices();
      // Filter enabled voices client-side
      setVoices(voicesData.filter(voice => voice.enabled));
    } catch (error) {
      console.error('Error fetching voices:', error);
    }
  };

  const handleOpenDialog = (panelist?: Panelist) => {
    if (panelist) {
      setEditingPanelist(panelist);
      setFormData({
        name: panelist.name,
        gender: panelist.gender,
        age: panelist.age || 30,
        ttsProvider: panelist.ttsProvider,
        voiceId: panelist.voiceId || '',
        voiceName: panelist.voiceName,
        languageCode: panelist.languageCode,
        character: panelist.character,
        background: panelist.background || '',
        expertise: panelist.expertise,
        discussionStyle: {
          tone: panelist.discussionStyle.tone || 'professional',
          pace: panelist.discussionStyle.pace || 'moderate',
          style: panelist.discussionStyle.style || 'informative'
        },
        isPublic: panelist.isPublic,
      });
    } else {
      setEditingPanelist(null);
      setFormData({
        name: '',
        gender: 'female',
        age: 30,
        ttsProvider: 'google',
        voiceId: '',
        voiceName: '',
        languageCode: 'en-US',
        character: '',
        background: '',
        expertise: [],
        discussionStyle: {
          tone: 'professional',
          pace: 'moderate',
          style: 'informative'
        },
        isPublic: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPanelist(null);
    setError('');
  };

  const handleSubmit = async () => {
    try {
      if (editingPanelist) {
        await apiService.updatePanelist(editingPanelist._id, formData);
      } else {
        await apiService.createPanelist(formData);
      }
      handleCloseDialog();
      fetchPanelists();
    } catch (error) {
      console.error('Error saving panelist:', error);
      setError('Failed to save panelist');
    }
  };

  const handleDelete = async (panelistId: string) => {
    if (window.confirm('Are you sure you want to delete this panelist?')) {
      try {
        await apiService.deletePanelist(panelistId);
        fetchPanelists();
      } catch (error) {
        console.error('Error deleting panelist:', error);
        setError('Failed to delete panelist');
      }
    }
  };

  const handleExpertiseChange = (expertise: string) => {
    setFormData(prev => ({
      ...prev,
      expertise: prev.expertise.includes(expertise)
        ? prev.expertise.filter(e => e !== expertise)
        : [...prev.expertise, expertise]
    }));
  };

  const handleTTSProviderChange = (provider: string) => {
    setFormData(prev => ({
      ...prev,
      ttsProvider: provider,
      voiceId: '',
      voiceName: '',
      languageCode: provider === 'google' ? 'en-US' : 'en-US'
    }));
  };

  const handleVoiceChange = (voiceId: string) => {
    const selectedVoice = voices.find(v => v._id === voiceId);
    if (selectedVoice) {
      setFormData(prev => ({
        ...prev,
        voiceId: selectedVoice._id,
        voiceName: selectedVoice.name,
        languageCode: selectedVoice.languageCodes[0] || 'en-US'
      }));
    }
  };

  const getFilteredVoices = () => {
    return voices.filter(voice => voice.source === formData.ttsProvider);
  };

  const handleHeadshotUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !editingPanelist) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image file size must be less than 5MB');
      return;
    }

    setUploadingHeadshot(true);
    setError('');

    try {
      const result = await apiService.uploadPanelistHeadshot(editingPanelist._id, file);
      
      // Update the panelist in the list
      setPanelists(prev => prev.map(p => 
        p._id === editingPanelist._id 
          ? { ...p, headshotUrl: result.headshotUrl }
          : p
      ));
      
      // Update the editing panelist
      setEditingPanelist(prev => prev ? { ...prev, headshotUrl: result.headshotUrl } : null);
      
    } catch (error) {
      console.error('Error uploading headshot:', error);
      setError('Failed to upload headshot');
    } finally {
      setUploadingHeadshot(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleHeadshotDelete = async () => {
    if (!editingPanelist?.headshotUrl) return;

    setDeletingHeadshot(true);
    setError('');

    try {
      await apiService.deletePanelistHeadshot(editingPanelist._id);
      
      // Update the panelist in the list
      setPanelists(prev => prev.map(p => 
        p._id === editingPanelist._id 
          ? { ...p, headshotUrl: undefined }
          : p
      ));
      
      // Update the editing panelist
      setEditingPanelist(prev => prev ? { ...prev, headshotUrl: undefined } : null);
      
    } catch (error) {
      console.error('Error deleting headshot:', error);
      setError('Failed to delete headshot');
    } finally {
      setDeletingHeadshot(false);
    }
  };



  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Panelist
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {panelists.map((panelist) => (
          <Grid item xs={12} sm={6} md={4} key={panelist._id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <Avatar
                    src={panelist.headshotUrl ? apiService.getPanelistHeadshotUrl(panelist._id) : undefined}
                    alt={panelist.name}
                    sx={{ 
                      width: 60, 
                      height: 60, 
                      mr: 2,
                      bgcolor: 'primary.main'
                    }}
                    onError={(e) => {
                      console.error('Avatar image failed to load:', e);
                      console.log('Image URL:', panelist.headshotUrl ? apiService.getPanelistHeadshotUrl(panelist._id) : 'No URL');
                    }}
                    onLoad={() => {
                      console.log('Avatar image loaded successfully');
                    }}
                  >
                    {panelist.name.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="h6">{panelist.name}</Typography>
                      <Box>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenDialog(panelist)}
                          title="Edit panelist"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(panelist._id)}
                          title="Delete panelist"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {panelist.character}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  {panelist.expertise.map((exp) => (
                    <Chip key={exp} label={exp} size="small" />
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Voice: {panelist.voiceName} • TTS: {panelist.ttsProvider}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingPanelist ? 'Edit Panelist' : 'Create Panelist'}
        </DialogTitle>
        <DialogContent>
          {/* Headshot Section - Only show for editing existing panelists */}
          {editingPanelist && (
            <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Headshot
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar
                  src={editingPanelist.headshotUrl ? apiService.getPanelistHeadshotUrl(editingPanelist._id) : undefined}
                  alt={editingPanelist.name}
                  sx={{ width: 80, height: 80, bgcolor: 'primary.main' }}
                  onError={(e) => {
                    console.error('Edit dialog Avatar image failed to load:', e);
                    console.log('Edit dialog Image URL:', editingPanelist.headshotUrl ? apiService.getPanelistHeadshotUrl(editingPanelist._id) : 'No URL');
                  }}
                  onLoad={() => {
                    console.log('Edit dialog Avatar image loaded successfully');
                  }}
                >
                  {editingPanelist.name.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleHeadshotUpload}
                    style={{ display: 'none' }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<PhotoCameraIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingHeadshot}
                    sx={{ mb: 1 }}
                  >
                    {uploadingHeadshot ? (
                      <CircularProgress size={20} />
                    ) : (
                      'Upload Headshot'
                    )}
                  </Button>
                  {editingPanelist.headshotUrl && (
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeletePhotoIcon />}
                      onClick={handleHeadshotDelete}
                      disabled={deletingHeadshot}
                      size="small"
                    >
                      {deletingHeadshot ? (
                        <CircularProgress size={16} />
                      ) : (
                        'Remove'
                      )}
                    </Button>
                  )}
                </Box>
              </Box>
              <Typography variant="caption" color="text.secondary">
                Upload a professional headshot (JPG, PNG, GIF, WebP). Max size: 5MB. Recommended: 400x400px or larger.
              </Typography>
            </Box>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Gender</InputLabel>
                <Select
                  value={formData.gender}
                  onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                  label="Gender"
                >
                  <MenuItem value="female">Female</MenuItem>
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="non_binary">Non-binary</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Age"
                type="number"
                value={formData.age}
                onChange={(e) => setFormData({ ...formData, age: parseInt(e.target.value) })}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>TTS Provider</InputLabel>
                <Select
                  value={formData.ttsProvider}
                  onChange={(e) => handleTTSProviderChange(e.target.value)}
                  label="TTS Provider"
                >
                  <MenuItem value="google">Google TTS</MenuItem>
                  <MenuItem value="openai">OpenAI TTS</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Voice</InputLabel>
                <Select
                  value={formData.voiceId}
                  onChange={(e) => handleVoiceChange(e.target.value)}
                  label="Voice"
                >
                  {getFilteredVoices().map((voice) => (
                    <MenuItem key={voice._id} value={voice._id}>
                      {voice.name} ({voice.ssmlGender}) - {voice.languageCodes.join(', ')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Character Description"
                multiline
                rows={3}
                value={formData.character}
                onChange={(e) => setFormData({ ...formData, character: e.target.value })}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Background"
                multiline
                rows={2}
                value={formData.background}
                onChange={(e) => setFormData({ ...formData, background: e.target.value })}
                margin="normal"
              />
            </Grid>
          </Grid>

          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            Expertise Areas
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {['Sports', 'Politics', 'Technology', 'Business', 'Entertainment', 'Science', 'Health'].map((exp) => (
              <Chip
                key={exp}
                label={exp}
                onClick={() => handleExpertiseChange(exp)}
                color={formData.expertise.includes(exp) ? 'primary' : 'default'}
                variant={formData.expertise.includes(exp) ? 'filled' : 'outlined'}
                size="small"
              />
            ))}
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={formData.isPublic}
                onChange={(e) => setFormData({ ...formData, isPublic: e.target.checked })}
              />
            }
            label="Active"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingPanelist ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Panelists;
